import type { PendingLinkedHiByIndex } from './psLinkedHiSave';
import { shouldReusePendingLinkedHi, shouldSkipExistingHiUpdate } from './psLinkedHiSave';

export type PsEditWoForHiPlan = {
  includeHomeInspection?: boolean | null;
  linkedHIWorkOrderID?: string | null;
};

export type PsEditHiAction =
  | { kind: 'keep'; index: number; id: string }
  | { kind: 'patch'; index: number; id: string }
  | { kind: 'reusePending'; index: number; id: string }
  | { kind: 'create'; index: number };

export type PsEditLinkedHiPlan = {
  /** Seed map: unchecked indexes set to null; included indexes filled after actions run. */
  linkedHiByIndex: Record<number, string | null | undefined>;
  indexesNeedingHiApi: number[];
  actions: PsEditHiAction[];
  needsNewHiCreate: boolean;
  /** Pending HI ids the user unchecked (orphans); warn but do not force re-link on edit. */
  abandonedPendingIds: string[];
};

/**
 * Pure planner for Process Service edit Include-HI save (issue #421).
 * Unlike create, explicit uncheck is respected even when a pending HI exists.
 */
export const planPsEditLinkedHiSave = (input: {
  workOrders: PsEditWoForHiPlan[];
  pendingByIndex: PendingLinkedHiByIndex;
  baselineUpdateFingerprints: Record<number, string>;
  nextUpdateFingerprints: Record<number, string>;
  nextCreateFingerprints: Record<number, string>;
}): PsEditLinkedHiPlan => {
  const linkedHiByIndex: Record<number, string | null | undefined> = {};
  const actions: PsEditHiAction[] = [];
  const abandonedPendingIds: string[] = [];

  input.workOrders.forEach((wo, index) => {
    const include = Boolean(wo.includeHomeInspection);
    const pending = input.pendingByIndex[index];

    if (!include) {
      linkedHiByIndex[index] = null;
      if (pending?.id) abandonedPendingIds.push(pending.id);
      return;
    }

    const linkedId = wo.linkedHIWorkOrderID ?? null;
    if (linkedId) {
      const nextFp = input.nextUpdateFingerprints[index] ?? '';
      if (shouldSkipExistingHiUpdate(input.baselineUpdateFingerprints[index], nextFp)) {
        actions.push({ kind: 'keep', index, id: linkedId });
      } else {
        actions.push({ kind: 'patch', index, id: linkedId });
      }
      return;
    }

    const createFp = input.nextCreateFingerprints[index] ?? '';
    if (shouldReusePendingLinkedHi(pending, createFp)) {
      actions.push({ kind: 'reusePending', index, id: pending.id });
      return;
    }

    actions.push({ kind: 'create', index });
  });

  const indexesNeedingHiApi = actions.map((a) => a.index);
  const needsNewHiCreate = actions.some((a) => a.kind === 'create');

  return {
    linkedHiByIndex,
    indexesNeedingHiApi,
    actions,
    needsNewHiCreate,
    abandonedPendingIds,
  };
};
